import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import NavigationParticuliers from '@/components/NavigationParticuliers';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  Filter,
  Star,
  CheckCircle,
  Shield,
  Eye,
  ChevronDown,
  X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const VelosReconditionnésRedesign = () => {
  const [selectedType, setSelectedType] = useState('all');
  const [selectedWheelSize, setSelectedWheelSize] = useState('all');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [selectedFrameSize, setSelectedFrameSize] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const resetFilters = () => {
    setSelectedType('all');
    setSelectedWheelSize('all');
    setSelectedCondition('all');
    setSelectedFrameSize('all');
    setSelectedPriceRange('all');
  };

  const bikes = [
    {
      id: 'velo-001',
      name: 'Elops 500E',
      brand: 'Decathlon',
      fullName: 'Decathlon Elops 500E',
      year: '2022',
      image: './public/images/velo_ville_classique_reconditionne_20260211_125242_20260225_142435.png',
      condition: 'excellent',
      conditionLabel: 'Excellent',
      bikeType: 'ville',
      bikeTypeLabel: 'Ville',
      wheelSize: '28',
      frameSize: 'M',
      price: 280,
      originalPrice: 450,
      discount: 38,
      priceRange: 'low',
      available: true
    },
    {
      id: 'velo-002',
      name: 'Reaction Hybrid Pro',
      brand: 'Cube',
      fullName: 'Cube Reaction Hybrid Pro',
      year: '2023',
      image: './public/images/vtt_tout_terrain_reconditionne_20260211_125856_20260225_142432.png',
      condition: 'very-good',
      conditionLabel: 'Très bon',
      bikeType: 'vtt',
      bikeTypeLabel: 'VTT',
      wheelSize: '27.5',
      frameSize: 'L',
      price: 1320,
      originalPrice: 2200,
      discount: 40,
      priceRange: 'medium',
      available: true
    },
    {
      id: 'velo-003',
      name: 'Samedi 27 Xroad',
      brand: 'Moustache',
      fullName: 'Moustache Samedi 27 Xroad',
      year: '2023',
      image: './public/images/velo_electrique_urbain_reconditionne_20260211_125856_1_20260225_142429.png',
      condition: 'excellent',
      conditionLabel: 'Excellent',
      bikeType: 'vtc',
      bikeTypeLabel: 'VTC',
      wheelSize: '28',
      frameSize: 'M',
      price: 1680,
      originalPrice: 2800,
      discount: 40,
      priceRange: 'high',
      available: true
    },
    {
      id: 'velo-004',
      name: 'Original 500',
      brand: 'Btwin',
      fullName: 'Btwin Original 500',
      year: '2023',
      image: './public/images/velo_enfant_24_pouces_reconditionne_20260211_125856_2_20260225_142429.png',
      condition: 'good',
      conditionLabel: 'Bon',
      bikeType: 'enfant',
      bikeTypeLabel: 'Enfant',
      wheelSize: '24',
      frameSize: 'XS',
      price: 150,
      originalPrice: 280,
      discount: 46,
      priceRange: 'low',
      available: true
    },
    {
      id: 'velo-005',
      name: 'Load 60',
      brand: 'Riese & Müller',
      fullName: 'Riese & Müller Load 60',
      year: '2022',
      image: './public/images/velo_cargo_familial_reconditionne_20260211_125856_6_20260225_142454.png',
      condition: 'very-good',
      conditionLabel: 'Très bon',
      bikeType: 'cargo',
      bikeTypeLabel: 'Cargo',
      wheelSize: '20/26',
      frameSize: 'L',
      price: 2850,
      originalPrice: 4500,
      discount: 37,
      priceRange: 'premium',
      available: true
    },
    {
      id: 'velo-006',
      name: 'Electric C Line',
      brand: 'Brompton',
      fullName: 'Brompton Electric C Line',
      year: '2023',
      image: './public/images/velo_pliant_compact_reconditionne_20260211_125856_5_20260225_142453.png',
      condition: 'excellent',
      conditionLabel: 'Excellent',
      bikeType: 'pliant',
      bikeTypeLabel: 'Pliant',
      wheelSize: '16',
      frameSize: 'S',
      price: 1950,
      originalPrice: 3200,
      discount: 39,
      priceRange: 'high',
      available: true
    }
  ];

  const filteredBikes = bikes.filter(bike => {
    const typeMatch = selectedType === 'all' || bike.bikeType === selectedType;
    const wheelSizeMatch = selectedWheelSize === 'all' || bike.wheelSize === selectedWheelSize;
    const conditionMatch = selectedCondition === 'all' || bike.condition === selectedCondition;
    const frameSizeMatch = selectedFrameSize === 'all' || bike.frameSize === selectedFrameSize;
    const priceMatch = selectedPriceRange === 'all' || bike.priceRange === selectedPriceRange;
    return typeMatch && wheelSizeMatch && conditionMatch && frameSizeMatch && priceMatch;
  });

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return 'bg-green-50 text-green-700 border-green-200';
      case 'very-good': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'good': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NavigationParticuliers />
      
      {/* HERO MINIMALISTE */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Vélos reconditionnés
              </h1>
              
              <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
                Des vélos de qualité, reconditionnés par nos experts, 
                avec 1 an de garantie et 14 jours pour changer d'avis.
              </p>
              
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Jusqu'à -60% par rapport au neuf</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <span>Garantie 1 an</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <span>5/5 sur Google</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FILTRES ÉPURÉS */}
      <section className="bg-gray-50/50 py-6 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Filters */}
          <div className="hidden md:flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter className="h-4 w-4" />
              <span>Filtrer :</span>
            </div>
            
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-36 h-9 bg-white border-gray-200 text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="ville">Ville</SelectItem>
                <SelectItem value="vtc">VTC</SelectItem>
                <SelectItem value="vtt">VTT</SelectItem>
                <SelectItem value="cargo">Cargo</SelectItem>
                <SelectItem value="pliant">Pliant</SelectItem>
                <SelectItem value="enfant">Enfant</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedWheelSize} onValueChange={setSelectedWheelSize}>
              <SelectTrigger className="w-32 h-9 bg-white border-gray-200 text-sm">
                <SelectValue placeholder="Roues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="16">16"</SelectItem>
                <SelectItem value="20">20"</SelectItem>
                <SelectItem value="24">24"</SelectItem>
                <SelectItem value="26">26"</SelectItem>
                <SelectItem value="27.5">27.5"</SelectItem>
                <SelectItem value="28">28"</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedCondition} onValueChange={setSelectedCondition}>
              <SelectTrigger className="w-32 h-9 bg-white border-gray-200 text-sm">
                <SelectValue placeholder="État" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="excellent">Excellent</SelectItem>
                <SelectItem value="very-good">Très bon</SelectItem>
                <SelectItem value="good">Bon</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedPriceRange} onValueChange={setSelectedPriceRange}>
              <SelectTrigger className="w-36 h-9 bg-white border-gray-200 text-sm">
                <SelectValue placeholder="Prix" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous prix</SelectItem>
                <SelectItem value="low">Moins de 500€</SelectItem>
                <SelectItem value="medium">500€ - 1500€</SelectItem>
                <SelectItem value="high">1500€ - 2500€</SelectItem>
                <SelectItem value="premium">Plus de 2500€</SelectItem>
              </SelectContent>
            </Select>

            {(selectedType !== 'all' || selectedWheelSize !== 'all' || selectedCondition !== 'all' || selectedPriceRange !== 'all') && (
              <Button 
                variant="ghost" 
                onClick={resetFilters}
                className="text-sm text-gray-600 hover:text-gray-900 h-9 px-3"
              >
                Réinitialiser
              </Button>
            )}

            <div className="ml-auto text-sm text-gray-600">
              {filteredBikes.length} vélo{filteredBikes.length > 1 ? 's' : ''}
            </div>
          </div>

          {/* Mobile Filter Button */}
          <div className="md:hidden flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setMobileFiltersOpen(true)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtres
            </Button>
            <div className="text-sm text-gray-600">
              {filteredBikes.length} vélo{filteredBikes.length > 1 ? 's' : ''}
            </div>
          </div>

          {/* Mobile Filters Drawer */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              <div className="fixed inset-0 bg-black/50" onClick={() => setMobileFiltersOpen(false)} />
              <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl p-6 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg">Filtres</h3>
                  <Button variant="ghost" size="sm" onClick={() => setMobileFiltersOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Type</label>
                    <Select value={selectedType} onValueChange={setSelectedType}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Tous types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous types</SelectItem>
                        <SelectItem value="ville">Ville</SelectItem>
                        <SelectItem value="vtc">VTC</SelectItem>
                        <SelectItem value="vtt">VTT</SelectItem>
                        <SelectItem value="cargo">Cargo</SelectItem>
                        <SelectItem value="pliant">Pliant</SelectItem>
                        <SelectItem value="enfant">Enfant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">État</label>
                    <Select value={selectedCondition} onValueChange={setSelectedCondition}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Tous états" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous états</SelectItem>
                        <SelectItem value="excellent">Excellent</SelectItem>
                        <SelectItem value="very-good">Très bon</SelectItem>
                        <SelectItem value="good">Bon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Prix</label>
                    <Select value={selectedPriceRange} onValueChange={setSelectedPriceRange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Tous prix" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous prix</SelectItem>
                        <SelectItem value="low">Moins de 500€</SelectItem>
                        <SelectItem value="medium">500€ - 1500€</SelectItem>
                        <SelectItem value="high">1500€ - 2500€</SelectItem>
                        <SelectItem value="premium">Plus de 2500€</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={resetFilters}
                    className="flex-1"
                  >
                    Réinitialiser
                  </Button>
                  <Button 
                    onClick={() => setMobileFiltersOpen(false)}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    Appliquer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* GRILLE MINIMALISTE */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBikes.map((bike) => (
              <ScrollReveal key={bike.id}>
                <Card className="group border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 bg-white overflow-hidden">
                  <div className="relative">
                    <img 
                      src={bike.image} 
                      alt={bike.fullName}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Badges discrets */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1">
                      <Badge className={`${getConditionColor(bike.condition)} text-xs font-medium border`}>
                        {bike.conditionLabel}
                      </Badge>
                    </div>
                    
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-red-500 text-white text-xs font-semibold">
                        -{bike.discount}%
                      </Badge>
                    </div>

                    <div className="absolute bottom-3 left-3">
                      <Badge variant="secondary" className="text-xs">
                        {bike.bikeTypeLabel} • {bike.wheelSize}"
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">{bike.brand}</p>
                      <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-2">
                        {bike.name}
                      </h3>
                    </div>
                    
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-bold text-primary">
                          {bike.price}€
                        </span>
                        <span className="text-sm text-gray-500 line-through">
                          {bike.originalPrice}€
                        </span>
                      </div>
                    </div>
                    
                    <Button 
                      asChild 
                      variant="outline"
                      className="w-full text-sm border-gray-300 hover:border-primary hover:text-primary"
                    >
                      <Link to={`/velos-occasion/${bike.id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        Voir la fiche
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
          
          {filteredBikes.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Filter className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucun vélo trouvé
              </h3>
              <p className="text-gray-600 mb-6">
                Essayez de modifier vos critères de recherche.
              </p>
              <Button onClick={resetFilters} variant="outline">
                Réinitialiser les filtres
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* BLOC CONFIANCE */}
      <section className="py-16 bg-gray-50/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
                ))}
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Noté 5/5 sur Google
              </h2>
              
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Nos clients nous font confiance pour la qualité de nos vélos reconditionnés 
                et notre service personnalisé.
              </p>
              
              <Button 
                variant="outline" 
                className="border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
              >
                <Star className="h-4 w-4 mr-2" />
                Voir nos avis Google
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
};

export default VelosReconditionnésRedesign;